import { SEASON_ANCHORS } from './seasonAnchors';

export type SeasonTerm = '春分' | '夏至' | '秋分' | '冬至';
export type SeasonLabel = '春季' | '夏季' | '秋季' | '冬季';
export type SeasonName = '春天' | '夏天' | '秋天' | '冬天';
export type SeasonKey = 'spring' | 'summer' | 'autumn' | 'winter';

export interface SeasonContext {
  term: SeasonTerm;
  seasonLabel: SeasonLabel;
  seasonName: SeasonName;
  anchorDate: string;
}

const SEASON_CONTEXTS: Record<SeasonKey, Omit<SeasonContext, 'anchorDate'>> = {
  spring: { term: '春分', seasonLabel: '春季', seasonName: '春天' },
  summer: { term: '夏至', seasonLabel: '夏季', seasonName: '夏天' },
  autumn: { term: '秋分', seasonLabel: '秋季', seasonName: '秋天' },
  winter: { term: '冬至', seasonLabel: '冬季', seasonName: '冬天' }
};

const DAY_MS = 24 * 60 * 60 * 1000;

const parseDateInput = (input?: Date | string | number): Date => {
  if (input instanceof Date) {
    if (!Number.isNaN(input.getTime())) {
      return input;
    }
    return new Date();
  }

  if (input !== undefined) {
    const parsed = new Date(input);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }
  }

  return new Date();
};

const getShanghaiDateParts = (date: Date): { year: number; month: number; day: number } => {
  try {
    const formatter = new Intl.DateTimeFormat('zh-CN', {
      timeZone: 'Asia/Shanghai',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric'
    });
    const parts = formatter.formatToParts(date);
    const year = Number(parts.find(part => part.type === 'year')?.value);
    const month = Number(parts.find(part => part.type === 'month')?.value);
    const day = Number(parts.find(part => part.type === 'day')?.value);

    if (!Number.isNaN(year) && !Number.isNaN(month) && !Number.isNaN(day)) {
      return { year, month, day };
    }
  } catch (error) {
    // Fallback to local date if Intl/timeZone is unavailable.
  }

  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate()
  };
};

const toSerialDay = (year: number, month: number, day: number): number => {
  return Math.floor(Date.UTC(year, month - 1, day) / DAY_MS);
};

const parseAnchorSerialDay = (dateString: string): number => {
  const [yearStr, monthStr, dayStr] = dateString.split('-');
  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);
  return toSerialDay(year, month, day);
};

const ANCHORS_WITH_SERIAL = SEASON_ANCHORS.map(anchor => ({
  ...anchor,
  serial: parseAnchorSerialDay(anchor.date)
}));

const FIRST_ANCHOR = ANCHORS_WITH_SERIAL[0];
const LAST_ANCHOR = ANCHORS_WITH_SERIAL[ANCHORS_WITH_SERIAL.length - 1];

const buildSeasonContext = (term: SeasonKey, anchorDate: string): SeasonContext => {
  return {
    ...SEASON_CONTEXTS[term],
    anchorDate
  };
};

export const getSeasonContext = (input?: Date | string | number): SeasonContext => {
  const date = parseDateInput(input);
  const { year, month, day } = getShanghaiDateParts(date);
  const targetSerial = toSerialDay(year, month, day);

  if (targetSerial <= FIRST_ANCHOR.serial) {
    return buildSeasonContext(FIRST_ANCHOR.term as SeasonKey, FIRST_ANCHOR.date);
  }
  if (targetSerial >= LAST_ANCHOR.serial) {
    return buildSeasonContext(LAST_ANCHOR.term as SeasonKey, LAST_ANCHOR.date);
  }

  let best = FIRST_ANCHOR;
  let bestDelta = Math.abs(best.serial - targetSerial);
  let bestIsFuture = best.serial >= targetSerial;

  for (const anchor of ANCHORS_WITH_SERIAL) {
    const delta = Math.abs(anchor.serial - targetSerial);
    if (delta < bestDelta) {
      best = anchor;
      bestDelta = delta;
      bestIsFuture = anchor.serial >= targetSerial;
      continue;
    }

    if (delta === bestDelta) {
      const isFuture = anchor.serial >= targetSerial;
      if (isFuture && !bestIsFuture) {
        best = anchor;
        bestIsFuture = true;
      } else if (isFuture === bestIsFuture && anchor.serial < best.serial) {
        best = anchor;
        bestIsFuture = isFuture;
      }
    }
  }

  return buildSeasonContext(best.term as SeasonKey, best.date);
};

export const formatSeasonAnchorDate = (anchorDate: string, delimiter: string = '.'): string => {
  return anchorDate.replace(/-/g, delimiter);
};

export const replaceSeasonTokens = (text: string, context: SeasonContext): string => {
  return text
    .replace(/\{\{\s*seasonTerm\s*\}\}/g, context.term)
    .replace(/\{\{\s*seasonLabel\s*\}\}/g, context.seasonLabel)
    .replace(/\{\{\s*seasonName\s*\}\}/g, context.seasonName);
};
